import { useEffect, useState } from "react";

import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import ButtonSecondarySmall from "../../../DesignSystem/Buttons/ButtonSecondarySmall";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import CheckboxGroup from "../../../DesignSystem/Inputs/CheckboxGroup";
import ToggleButton from "../../../DesignSystem/Inputs/ToggleButton";

import AnnouncementFormSection from "../components/AnnouncementFormSection";
import DateTimeField from "../../../DesignSystem/Inputs/DateTimeField";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import Banner from "../../../DesignSystem/Banner/Banner";

const ANNOUNCEMENT_FOR_OPTIONS = [
  "FP",
  "MA",
  "GI/Prot",
  "ER",
  "NAP",
  "All Advisers",
  "Admin",
];

const EMPTY_INITIAL_VALUES = {};

const DEFAULT_ANNOUNCEMENT = {
  announcementFor: [],
  scheduleAnnouncement: false,
  scheduleDate: "",
  scheduleTime: "",
  keepUntilDate: "",
  keepUntilTime: "",
  notifyOnLogin: false,

  contentType: "",
  contentTitle: "",
  content: "",

  headerImageFiles: [],
  acknowledgementType: "acknowledged",
  acknowledgementText: "I have read through the details of this announcement.",

  url: "",
  documentFiles: [],
};
const getStatusLabel = (status) => {
  if (status === "draft") return "Draft";
  if (status === "scheduled") return "Scheduled";
  if (status === "archived") return "Archived";
  return "Active";
};

const getPrimaryButtonLabel = ({ mode, status, scheduleAnnouncement }) => {
  if (mode === "create") {
    return scheduleAnnouncement ? "Schedule Announcement" : "Send Announcement";
  }

  if (mode === "edit") {
    if (status === "draft") {
      return scheduleAnnouncement
        ? "Schedule Announcement"
        : "Send Announcement";
    }

    if (status === "scheduled") {
      return "Update Scheduled Announcement";
    }

    if (status === "active") {
      return "Update Announcement";
    }

    return "Update Announcement";
  }

  return "";
};
const AnnouncementDetailsForm = ({
  initialValues = EMPTY_INITIAL_VALUES,
  mode = "create",
  loading = false,
  onSaveDraft,
  onSubmit,
  readOnlyMessage = "",
}) => {
  const isReviewMode = mode === "review";
  const canSaveDraft = mode === "create" || mode === "edit";

  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    ...DEFAULT_ANNOUNCEMENT,
    ...initialValues,
  });

  useEffect(() => {
    setFormData({
      ...DEFAULT_ANNOUNCEMENT,
      ...initialValues,
    });

    setError("");
  }, [initialValues]);

  const updateField = (field, value) => {
    setError("");

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const normaliseFiles = (files) => {
    if (!files) return [];
    return Array.isArray(files) ? files : [files];
  };

  const handleAnnouncementForChange = (e, formDataKey, option) => {
    const checked = e.target.checked;

    setError("");

    setFormData((prev) => {
      const current = prev.announcementFor || [];

      return {
        ...prev,
        announcementFor: checked
          ? Array.from(new Set([...current, option]))
          : current.filter((item) => item !== option),
      };
    });
  };

  const validateAnnouncement = () => {
    if (!formData.announcementFor?.length) {
      return "Please select who this announcement is for.";
    }

    if (formData.scheduleAnnouncement) {
      if (!formData.scheduleDate) {
        return "Please select when the announcement should be sent.";
      }

      if (!formData.scheduleTime) {
        return "Please select the scheduled announcement time.";
      }
    }

    if (!formData.contentType?.trim()) {
      return "Please enter the content type.";
    }

    if (!formData.contentTitle?.trim()) {
      return "Please enter the content title.";
    }

    if (!formData.content?.trim()) {
      return "Please enter the announcement content.";
    }

    if (!formData.headerImageFiles?.length) {
      return "Please upload a header image.";
    }

    if (formData.acknowledgementType === "read_more") {
      const hasUrl = !!formData.url?.trim();
      const hasDocument = formData.documentFiles?.length > 0;

      if (!hasUrl && !hasDocument) {
        return "Please add a URL or upload a document for the Read More button.";
      }
    }

    return "";
  };

  const handlePrimaryClick = () => {
    if (loading || isReviewMode) return;

    const validationError = validateAnnouncement();

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    onSubmit?.(formData);
  };

  const handleDraftClick = () => {
    if (loading || isReviewMode) return;

    setError("");
    onSaveDraft?.({
      ...formData,
      status: "draft",
    });
  };

  const handleArchiveClick = () => {
    if (loading || !isReviewMode) return;

    setError("");
    onSubmit?.(formData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isReviewMode) return;

    handlePrimaryClick();
  };

const currentStatus = initialValues?.status || formData.status || "active";
const statusLabel = getStatusLabel(currentStatus);

const primaryLabel = getPrimaryButtonLabel({
  mode,
  status: currentStatus,
  scheduleAnnouncement: formData.scheduleAnnouncement,
});


  // const primaryLabel =
  //   mode === "edit" ? "Update Announcement" : "Send Announcement";

  return (
    <div
      className={`announcement-details-form-wrap ${
        isReviewMode ? "announcement-details-form-wrap--readonly" : ""
      }`}
    >
      <form className="announcement-form" onSubmit={handleSubmit}>
        {isReviewMode && (
          <Banner
            type="warning"
            message={
              readOnlyMessage ||
              "You can view the details, but changes are locked because the announcement is active or archived."
            }
          />
        )}

        <AnnouncementFormSection title="Post Management">
          <div className="fp-form-column">
            <label className="FP_Body">
              Announcement For <span className="fp-form-required">*</span>
            </label>

            <div className="FP_Help_Text black">
              This will only be shown to the advisers selected below.
            </div>

            <CheckboxGroup
              options={ANNOUNCEMENT_FOR_OPTIONS}
              columns={7}
              formDataKey="announcementFor"
              selectedOptions={formData.announcementFor}
              disabled={loading || isReviewMode}
              handleCheckboxChange={handleAnnouncementForChange}
            />
          </div>

          <div className="announcement-toggle-row">
            <span className="FP_Body">Schedule Announcement</span>
            <ToggleButton
              checked={formData.scheduleAnnouncement}
              onChange={(value) => updateField("scheduleAnnouncement", value)}
              disabled={loading || isReviewMode}
            />
          </div>

          {formData.scheduleAnnouncement && (
            <DateTimeField
              label="When to Announce"
              date={formData.scheduleDate}
              time={formData.scheduleTime}
              onDateChange={(value) => updateField("scheduleDate", value)}
              onTimeChange={(value) => updateField("scheduleTime", value)}
              disabled={loading || isReviewMode}
            />
          )}

          {formData.scheduleAnnouncement && (
            <DateTimeField
              label="Keep Announcement Until"
              date={formData.keepUntilDate}
              time={formData.keepUntilTime}
              onDateChange={(value) => updateField("keepUntilDate", value)}
              onTimeChange={(value) => updateField("keepUntilTime", value)}
              disabled={loading || isReviewMode}
            />
          )}

          <div className="announcement-toggle-row">
            <span className="FP_Body">
              Notify first time with a popup upon login
            </span>
            <ToggleButton
              checked={formData.notifyOnLogin}
              onChange={(value) => updateField("notifyOnLogin", value)}
              disabled={loading || isReviewMode}
            />
          </div>
        </AnnouncementFormSection>

        <AnnouncementFormSection title="Content Management">
          <div className="fp-form-column">
            <label className="fp-form-label FP_Body">
              Content Type <span className="fp-form-required">*</span>
            </label>

            <div className="FP_Content">
              Newsletter, Compliance Bulletin, Business Referrals, etc.
            </div>

            <input
              type="text"
              value={formData.contentType}
              onChange={(e) => updateField("contentType", e.target.value)}
              placeholder="Content Type"
              className="FP_Content fp-form-input"
              disabled={loading || isReviewMode}
            />
          </div>

          <div className="fp-form-column">
            <label className="fp-form-label FP_Body">
              Content Title <span className="fp-form-required">*</span>
            </label>

            <input
              type="text"
              value={formData.contentTitle}
              onChange={(e) => updateField("contentTitle", e.target.value)}
              placeholder="Content Title"
              className="FP_Content fp-form-input"
              disabled={loading || isReviewMode}
            />
          </div>

          <div className="fp-form-column">
            <label className="fp-form-label FP_Body">
              Content <span className="fp-form-required">*</span>
            </label>

            <div className="FP_Content">Overview of the announcement</div>

            <div className="announcement-textarea-wrap">
              <textarea
                value={formData.content}
                onChange={(e) =>
                  updateField("content", e.target.value.slice(0, 300))
                }
                placeholder="Enter your content here..."
                className="fp-form-input fp-form-textarea FP_Content announcement-content-textarea"
                disabled={loading || isReviewMode}
              />

              <span className="announcement-character-count">
                {formData.content.length}/300 characters
              </span>
            </div>
          </div>

          <div className="fp-form-column">
            <label className="fp-form-label FP_Body">
              Header Image <span className="fp-form-required">*</span>
            </label>

            <FileUpload
              label={null}
              description="Drag & drop files here or"
              browseText="Browse"
              status="default"
              disabled={loading || isReviewMode}
              accept=".png,.jpg,.jpeg"
              multiple={false}
              files={formData.headerImageFiles}
              onFilesChange={(files) =>
                updateField("headerImageFiles", normaliseFiles(files))
              }
            />

            <div className="FP_Content announcement-file-helper">
              Accepted file types: png, jpg, jpeg
            </div>

            <UploadedFileList
              files={formData.headerImageFiles}
              onRemove={(file, index) => {
                const newFiles = [...formData.headerImageFiles];
                newFiles.splice(index, 1);
                updateField("headerImageFiles", newFiles);
              }}
            />
          </div>

          <div className="fp-form-column">
            <label className="fp-form-label FP_Body">Acknowledgement</label>

            <div className="FP_Help_Text black">
              Choose acknowledgement button type. Select ‘Read More’ to add a
              URL link if it needs to connect to Mail Chimp / Members Hub.
            </div>

            <div className="announcement-acknowledgement-row">
              <label className="announcement-radio-label FP_Content">
                <input
                  type="radio"
                  name="acknowledgementType"
                  value="acknowledged"
                  checked={formData.acknowledgementType === "acknowledged"}
                  onChange={(e) =>
                    updateField("acknowledgementType", e.target.value)
                  }
                  disabled={loading || isReviewMode}
                />
                Acknowledged
              </label>

              <input
                type="text"
                value={formData.acknowledgementText}
                onChange={(e) =>
                  updateField("acknowledgementText", e.target.value)
                }
                className="FP_Content fp-form-input announcement-acknowledgement-input"
                disabled={loading || isReviewMode}
              />
            </div>
          </div>

          <label className="announcement-radio-label FP_Content">
            <input
              type="radio"
              name="acknowledgementType"
              value="read_more"
              checked={formData.acknowledgementType === "read_more"}
              onChange={(e) =>
                updateField("acknowledgementType", e.target.value)
              }
              disabled={loading || isReviewMode}
            />
            Read More
          </label>

          <div className="fp-form-column">
            <label className="FP_Help_Text black">
              Add URL or upload document to be opened upon clicking the button.
            </label>

            <input
              type="text"
              value={formData.url}
              onChange={(e) => updateField("url", e.target.value)}
              placeholder="URL link"
              className="FP_Content fp-form-input"
              disabled={loading || isReviewMode}
            />

            <FileUpload
              label={null}
              description="Drag & drop files here or"
              browseText="Browse"
              status="default"
              disabled={loading || isReviewMode}
              accept=".doc,.docx,.pdf"
              multiple={false}
              files={formData.documentFiles}
              onFilesChange={(files) =>
                updateField("documentFiles", normaliseFiles(files))
              }
            />

            <div className="FP_Content announcement-file-helper">
              Accepted file types: word doc, pdf
            </div>

            <UploadedFileList
              files={formData.documentFiles}
              onRemove={(file, index) => {
                const newFiles = [...formData.documentFiles];
                newFiles.splice(index, 1);
                updateField("documentFiles", newFiles);
              }}
            />
          </div>
        </AnnouncementFormSection>

        {error && <Banner type="error" message="Please fill out all required fields." />}

        <div className="announcement-actions">
          {canSaveDraft && (
            <ButtonSecondarySmall
              type="button"
              label="Save as Draft"
              onClick={handleDraftClick}
              disabled={loading}
            />
          )}

          {!isReviewMode && (
            <PrimaryButton
              type="button"
              label={primaryLabel}
              onClick={handlePrimaryClick}
              loading={loading}
              disabled={loading}
            />
          )}

          {isReviewMode && (
            <PrimaryButton
              type="button"
              label="Archive Announcement"
              onClick={handleArchiveClick}
              loading={loading}
              disabled={loading}
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default AnnouncementDetailsForm;
